// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

interface AnalysisRequest {
  analysisType: 'personal' | 'relationship' | 'astro' | 'healing' | 'psychological';
  personalInfo: {
    name: string;
    birthDate: string;
    birthTime?: string;
    birthPlace?: string;
    additionalInfo?: any;
  };
  partnerInfo?: {
    name: string;
    birthDate: string;
    relationshipType?: string;
  };
  focusArea?: string;
  healthConcern?: string;
}

interface AnalysisResult {
  id: string;
  analysisType: string;
  personalInfo: {
    name: string;
    birthDate: string;
    additionalInfo?: any;
  };
  results: {
    title: string;
    value: string | number;
    description: string;
    significance: 'high' | 'medium' | 'low';
    category: string;
  }[];
  insights: {
    title: string;
    content: string;
    type: 'strength' | 'challenge' | 'opportunity' | 'guidance';
  }[];
  recommendations: string[];
  summary: string;
  createdAt: string;
}

interface FeedbackData {
  overallRating: number;
  categoryRatings: {
    accuracy: number;
    clarity: number;
    usefulness: number;
    completeness: number;
  };
  comment: string;
  wouldRecommend: boolean | null;
  mostHelpful: string;
  improvements: string;
}

interface FeedbackSubmission {
  analysisId: string;
  feedback: FeedbackData;
  submittedAt: string;
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem('authToken');
      
      const config: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers,
        },
        ...options,
      };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'API request failed');
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Submit analysis request
  async submitAnalysis(analysisData: AnalysisRequest): Promise<ApiResponse<{ analysisId: string }>> {
    return this.request('/analysis/submit', {
      method: 'POST',
      body: JSON.stringify(analysisData),
    });
  }

  // Get analysis result
  async getAnalysisResult(analysisId: string): Promise<ApiResponse<AnalysisResult>> {
    return this.request(`/analysis/result/${analysisId}`);
  }

  // Get user's analysis history
  async getAnalysisHistory(): Promise<ApiResponse<AnalysisResult[]>> {
    return this.request('/analysis/history');
  }

  // Delete analysis
  async deleteAnalysis(analysisId: string): Promise<ApiResponse<{ success: boolean }>> {
    return this.request(`/analysis/${analysisId}`, {
      method: 'DELETE',
    });
  }

  // Download analysis as PDF
  async downloadAnalysisPDF(analysisId: string): Promise<Blob | null> {
    try {
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_BASE_URL}/analysis/pdf/${analysisId}`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      return await response.blob();
    } catch (error) {
      console.error('PDF Download Error:', error);
      return null;
    }
  }

  // Share analysis
  async shareAnalysis(analysisId: string, shareOptions: {
    email?: string;
    platform?: 'email' | 'whatsapp' | 'telegram' | 'link';
  }): Promise<ApiResponse<{ shareUrl?: string; success: boolean }>> {
    return this.request('/analysis/share', {
      method: 'POST',
      body: JSON.stringify({
        analysisId,
        ...shareOptions,
      }),
    });
  }

  // Submit feedback for analysis
  async submitFeedback(analysisId: string, feedback: FeedbackData): Promise<ApiResponse<{ feedbackId: string }>> {
    return this.request('/analysis/feedback', {
      method: 'POST',
      body: JSON.stringify({
        analysisId,
        feedback,
        submittedAt: new Date().toISOString()
      }),
    });
  }

  // Get feedback for analysis (admin/analytics)
  async getAnalysisFeedback(analysisId: string): Promise<ApiResponse<FeedbackSubmission[]>> {
    return this.request(`/analysis/${analysisId}/feedback`);
  }

  // Get feedback statistics
  async getFeedbackStats(): Promise<ApiResponse<{
    averageRating: number;
    totalFeedbacks: number;
    categoryAverages: {
      accuracy: number;
      clarity: number;
      usefulness: number;
      completeness: number;
    };
    recommendationRate: number;
  }>> {
    return this.request('/analysis/feedback/stats');
  }
}

// Export singleton instance
export const apiService = new ApiService();

// Export types for use in components
export type { AnalysisRequest, AnalysisResult, ApiResponse, FeedbackData, FeedbackSubmission }; 